import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Handles the Emergent OAuth redirect callback.
 * Frontend lands at /auth/callback#session_id=<one-shot>.
 * We read the hash synchronously (before AuthProvider /me runs), POST to
 * /api/auth/google, store the JWT, and redirect to /.
 *
 * REMINDER: DO NOT HARDCODE THE URL OR ADD ANY FALLBACKS — THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
    const { loginGoogle } = useAuth();
    const navigate = useNavigate();
    const handled = useRef(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (handled.current) return;
        handled.current = true;

        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) {
            setError("No session_id in callback URL.");
            return;
        }
        const sessionId = decodeURIComponent(m[1]);

        loginGoogle(sessionId)
            .then(() => {
                // Strip the hash and go home.
                window.history.replaceState(
                    null,
                    "",
                    window.location.pathname,
                );
                navigate("/", { replace: true });
            })
            .catch((err) => {
                console.error("[auth-callback] failed", err);
                setError(
                    err?.response?.data?.detail ||
                        "Sign-in failed. Please try again.",
                );
            });
    }, [loginGoogle, navigate]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-white text-[#0A0A0A]">
            <div className="text-center">
                {error ? (
                    <>
                        <p
                            className="text-[14px] text-[#DC2626]"
                            data-testid="auth-callback-error"
                        >
                            {error}
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate("/login", { replace: true })}
                            className="mt-4 rounded-lg border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium hover:border-[#0A0A0A]"
                        >
                            Back to login
                        </button>
                    </>
                ) : (
                    <p className="text-[14px] text-[#737373]">
                        Signing you in…
                    </p>
                )}
            </div>
        </div>
    );
}
